<template>
  <div class="wizard-body columns content-box small-9">
    <form class="row" @submit.prevent="submit">
      <div class="medium-12 columns">
        <page-header
          :header-title="pageTitle"
          :header-content="pageDescription"
        />
      </div>

      <div class="medium-12 columns">
        <label :class="{ error: $v.form.name.$error }">
          {{ $t('OPPORTUNITY_PIPELINES.FORM.NAME') }}
          <input
            v-model.trim="form.name"
            type="text"
            :placeholder="$t('OPPORTUNITY_PIPELINES.FORM.NAME_PLACEHOLDER')"
            @input="$v.form.name.$touch"
          />
          <span v-if="$v.form.name.$error" class="message">
            {{ $t('OPPORTUNITY_PIPELINES.FORM.NAME_REQUIRED') }}
          </span>
        </label>

        <label>
          {{ $t('OPPORTUNITY_PIPELINES.FORM.DESCRIPTION') }}
          <textarea
            v-model.trim="form.description"
            rows="3"
            :placeholder="
              $t('OPPORTUNITY_PIPELINES.FORM.DESCRIPTION_PLACEHOLDER')
            "
          />
        </label>

        <label>{{ $t('OPPORTUNITY_PIPELINES.FORM.MANAGER') }}</label>
        <multiselect
          v-model="selectedManager"
          track-by="id"
          label="name"
          :placeholder="$t('OPPORTUNITY_PIPELINES.FORM.MANAGER_PLACEHOLDER')"
          :multiple="false"
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          deselect-label=""
          :close-on-select="true"
          :options="managers"
          :allow-empty="false"
        />

        <div class="currency-grid">
          <label>
            {{ $t('OPPORTUNITY_PIPELINES.FORM.CURRENCY') }}
            <select
              v-model="form.default_currency"
              :disabled="supportedCurrencies.length === 1"
            >
              <option
                v-for="currency in supportedCurrencies"
                :key="currency"
                :value="currency"
              >
                {{ currency }}
              </option>
            </select>
          </label>

          <label>
            {{ $t('OPPORTUNITY_PIPELINES.FORM.DEFAULT_VALUE') }}
            <decimal-input v-model="form.default_value" />
          </label>
        </div>
      </div>

      <div class="medium-12 columns button-row">
        <woot-button
          :is-loading="isSubmitting"
          :disabled="$v.form.name.$invalid"
        >
          {{ submitLabel }}
        </woot-button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import PageHeader from '../../SettingsSubPageHeader';
import router from '../../../../index';
import { patchDraft, readDraft } from './draftState';
import DecimalInput from 'dashboard/components/widgets/forms/DecimalInput.vue';
import {
  DEFAULT_CURRENCY,
  SUPPORTED_CURRENCIES,
  MANAGEMENT_ROLES,
} from './config';

export default {
  components: {
    DecimalInput,
    PageHeader,
  },
  mixins: [alertMixin],
  data() {
    return {
      form: {
        name: '',
        description: '',
        manager_id: null,
        default_currency: DEFAULT_CURRENCY,
        default_value: 0,
      },
      isSubmitting: false,
    };
  },
  validations: {
    form: {
      name: { required },
    },
  },
  computed: {
    ...mapGetters({
      agents: 'agents/getAgents',
    }),
    pipelineId() {
      return this.$route.params.pipelineId;
    },
    managers() {
      return (this.agents || []).filter(agent =>
        MANAGEMENT_ROLES.includes(agent.role)
      );
    },
    selectedManager: {
      get() {
        return (
          this.managers.find(agent => agent.id === this.form.manager_id) || null
        );
      },
      set(agent) {
        this.form.manager_id = agent ? agent.id : null;
      },
    },
    supportedCurrencies() {
      return SUPPORTED_CURRENCIES;
    },
    isEditMode() {
      return this.$route.name === 'settings_opportunity_pipelines_edit';
    },
    isNewDraftFlow() {
      return this.$route.name === 'settings_opportunity_pipelines_new';
    },
    pageTitle() {
      const key = this.isEditMode
        ? 'OPPORTUNITY_PIPELINES.CREATE_FLOW.EDIT.TITLE'
        : 'OPPORTUNITY_PIPELINES.CREATE_FLOW.CREATE.TITLE';
      return this.$t(key);
    },
    pageDescription() {
      const key = this.isEditMode
        ? 'OPPORTUNITY_PIPELINES.CREATE_FLOW.EDIT.DESC'
        : 'OPPORTUNITY_PIPELINES.CREATE_FLOW.CREATE.DESC';
      return this.$t(key);
    },
    submitLabel() {
      const key = this.isEditMode
        ? 'OPPORTUNITY_PIPELINES.CREATE_FLOW.EDIT.SUBMIT'
        : 'OPPORTUNITY_PIPELINES.CREATE_FLOW.CREATE.SUBMIT';
      return this.$t(key);
    },
  },
  mounted() {
    this.fetchAgents();
  },
  methods: {
    normalizeCurrency(currency) {
      return this.supportedCurrencies.includes(currency)
        ? currency
        : DEFAULT_CURRENCY;
    },
    async fetchPipeline() {
      if (!this.isEditMode || !this.pipelineId) {
        return;
      }

      this.isSubmitting = true;
      try {
        const pipeline = await this.$store.dispatch(
          'opportunityPipelines/show',
          this.pipelineId
        );
        this.form = {
          name: pipeline.name || '',
          description: pipeline.description || '',
          manager_id: pipeline.manager_id || this.form.manager_id,
          default_currency: this.normalizeCurrency(pipeline.default_currency),
          default_value: Number(pipeline.default_value || 0),
        };
      } catch (error) {
        this.showAlert(this.$t('OPPORTUNITY_PIPELINES.ERRORS.FETCH_PIPELINES'));
      } finally {
        this.isSubmitting = false;
      }
    },
    async fetchAgents() {
      try {
        await this.$store.dispatch('agents/get');

        if (this.isNewDraftFlow) {
          const draft = readDraft();
          this.form = {
            ...this.form,
            ...draft.basicInfo,
            default_currency: this.normalizeCurrency(
              draft.basicInfo?.default_currency
            ),
          };
        }

        if (!this.form.manager_id && this.managers.length) {
          this.form.manager_id = this.managers[0].id;
        }
        await this.fetchPipeline();
      } catch (error) {
        this.showAlert(this.$t('OPPORTUNITY_PIPELINES.ERRORS.FETCH_AGENTS'));
      }
    },
    async submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      if (this.isNewDraftFlow) {
        const payload = {
          ...this.form,
          default_currency: this.normalizeCurrency(this.form.default_currency),
        };

        patchDraft({
          basicInfo: {
            ...payload,
          },
        });

        router.replace({
          name: 'settings_opportunity_pipelines_new_users',
        });
        return;
      }

      this.isSubmitting = true;
      try {
        const payload = {
          ...this.form,
          default_currency: this.normalizeCurrency(this.form.default_currency),
        };

        const pipeline = this.isEditMode
          ? await this.$store.dispatch('opportunityPipelines/update', {
              id: this.pipelineId,
              pipeline: payload,
            })
          : await this.$store.dispatch('opportunityPipelines/create', {
              pipeline: payload,
            });

        router.replace({
          name: 'settings_opportunity_pipelines_users',
          params: { pipelineId: pipeline.id },
        });
      } catch (error) {
        this.showAlert(this.$t('OPPORTUNITY_PIPELINES.ERRORS.CREATE_PIPELINE'));
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.currency-grid {
  display: grid;
  gap: var(--space-small);
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.button-row {
  display: flex;
  gap: var(--space-small);
  justify-content: flex-end;
  margin-top: var(--space-small);
}
</style>
